"use strict";
Object.defineProperty(exports, Symbol.toStringTag, { value: "Module" });
const store = require("@tanstack/store");
const asyncRetryer = require("./async-retryer.cjs");
const utils = require("./utils.cjs");
const eventClient = require("./event-client.cjs");
function getDefaultAsyncBatcherState() {
  return {
    errorCount: 0,
    executeCount: 0,
    failedItems: [],
    isEmpty: true,
    isExecuting: false,
    isPending: false,
    items: [],
    lastResult: void 0,
    settleCount: 0,
    size: 0,
    status: "idle",
    successCount: 0,
    totalItemsProcessed: 0,
    totalItemsFailed: 0
  };
}
function asyncBatcherOptions(options) {
  return options;
}
const defaultOptions = {
  asyncRetryerOptions: {
    maxAttempts: 1
  },
  getShouldExecute: () => false,
  maxSize: Infinity,
  started: true,
  throwOnError: true,
  wait: Infinity
};
class AsyncBatcher {
  constructor(fn, initialOptions) {
    this.fn = fn;
    this.store = new store.Store(
      getDefaultAsyncBatcherState()
    );
    this.asyncRetryers = /* @__PURE__ */ new Map();
    this.#timeoutId = null;
    this._emit = () => eventClient.emitChange("AsyncBatcher", this);
    this.setOptions = (newOptions) => {
      this.options = { ...this.options, ...newOptions };
    };
    this.#setState = (newState) => {
      this.store.setState((state) => {
        const combinedState = {
          ...state,
          ...newState
        };
        const { isExecuting, isPending, items } = combinedState;
        const size = items.length;
        const isEmpty = size === 0;
        return {
          ...combinedState,
          isEmpty,
          size,
          status: isExecuting ? "executing" : isPending ? "pending" : isEmpty ? "idle" : "populated"
        };
      });
      eventClient.emitChange("AsyncBatcher", this);
    };
    this.#getWait = () => {
      return utils.parseFunctionOrValue(this.options.wait, this);
    };
    this.addItem = async (item) => {
      this.#setState({
        items: [...this.store.state.items, item],
        isPending: this.options.wait !== Infinity
      });
      this.options.onItemsChange?.(this);
      const shouldProcess = this.store.state.items.length >= this.options.maxSize || this.options.getShouldExecute(this.store.state.items, this);
      if (shouldProcess) {
        return await this.#execute();
      } else if (this.options.wait !== Infinity) {
        this.#clearTimeout();
        this.#timeoutId = setTimeout(() => this.#execute(), this.#getWait());
        await new Promise((resolve) => setTimeout(resolve, this.#getWait()));
      }
    };
    this.#execute = async () => {
      if (this.store.state.items.length === 0) {
        return void 0;
      }
      const currentExecuteCount = this.store.state.executeCount + 1;
      const batch = this.peekAllItems();
      this.clear();
      this.options.onItemsChange?.(this);
      this.#setState({ isExecuting: true, executeCount: currentExecuteCount });
      try {
        const currentAsyncRetryer = new asyncRetryer.AsyncRetryer(
          this.fn,
          this.options.asyncRetryerOptions
        );
        this.asyncRetryers.set(currentExecuteCount, currentAsyncRetryer);
        const result = await currentAsyncRetryer.execute(batch);
        this.#setState({
          totalItemsProcessed: this.store.state.totalItemsProcessed + batch.length,
          lastResult: result,
          successCount: this.store.state.successCount + 1
        });
        this.options.onSuccess?.(result, batch, this);
        return result;
      } catch (error) {
        this.#setState({
          errorCount: this.store.state.errorCount + 1,
          failedItems: [...this.store.state.failedItems, ...batch],
          totalItemsFailed: this.store.state.totalItemsFailed + batch.length
        });
        this.options.onError?.(error, batch, this);
        if (this.options.throwOnError) {
          throw error;
        }
        return void 0;
      } finally {
        this.asyncRetryers.delete(currentExecuteCount);
        this.#setState({
          isExecuting: false,
          settleCount: this.store.state.settleCount + 1
        });
        this.options.onSettled?.(batch, this);
      }
    };
    this.flush = async () => {
      this.#clearTimeout();
      return await this.#execute();
    };
    this.peekAllItems = () => {
      return [...this.store.state.items];
    };
    this.peekFailedItems = () => {
      return [...this.store.state.failedItems];
    };
    this.#clearTimeout = () => {
      if (this.#timeoutId) {
        clearTimeout(this.#timeoutId);
        this.#timeoutId = null;
      }
    };
    this.clear = () => {
      this.#setState({ items: [], failedItems: [], isPending: false });
    };
    this.abort = () => {
      this.asyncRetryers.forEach((retryer) => retryer.abort());
      this.asyncRetryers.clear();
      this.#setState({
        isExecuting: false
      });
    };
    this.cancel = () => {
      this.#clearTimeout();
      this.#setState({
        isPending: false
      });
    };
    this.reset = () => {
      this.#setState(getDefaultAsyncBatcherState());
      this.options.onItemsChange?.(this);
      this.asyncRetryers.forEach((retryer) => retryer.reset());
    };
    this.key = utils.createKey(initialOptions.key);
    this.options = {
      ...defaultOptions,
      ...initialOptions,
      throwOnError: initialOptions.throwOnError ?? !initialOptions.onError
    };
    this.#setState(this.options.initialState ?? {});
    eventClient.pacerEventClient.on("d-AsyncBatcher", (event) => {
      if (event.payload.key !== this.key) return;
      this.#setState(event.payload.store.state);
      this.setOptions(event.payload.options);
    });
  }
  #timeoutId;
  #setState;
  #getWait;
  #execute;
  #clearTimeout;
  /**
   * Returns the AbortSignal for a specific execution.
   * If no executeCount is provided, returns the signal for the most recent execution.
   * Returns null if no execution is found or not currently executing.
   *
   * @param executeCount - Optional specific execution to get signal for
   * @example
   * ```typescript
   * const batcher = new AsyncBatcher(
   *   async (items: string[]) => {
   *     const signal = batcher.getAbortSignal()
   *     if (signal) {
   *       const response = await fetch('/api/batch', {
   *         method: 'POST',
   *         body: JSON.stringify(items),
   *         signal
   *       })
   *       return response.json()
   *     }
   *   },
   *   { maxSize: 10, wait: 100 }
   * )
   * ```
   */
  getAbortSignal(executeCount) {
    const count = executeCount ?? this.store.state.executeCount;
    const retryer = this.asyncRetryers.get(count);
    return retryer?.getAbortSignal() ?? null;
  }
}
function asyncBatch(fn, options) {
  const batcher = new AsyncBatcher(fn, options);
  return batcher.addItem;
}
exports.AsyncBatcher = AsyncBatcher;
exports.asyncBatch = asyncBatch;
exports.asyncBatcherOptions = asyncBatcherOptions;
//# sourceMappingURL=async-batcher.cjs.map
